import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
  Menu,
  Dropdown
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined, DownOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import HolidayModel from './HolidayModel';
import ImportHolidayModal from './ImportHolidayModal';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';

const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

interface Holidays {
  holiday_id: number;
  holiday_name: string;
  holiday_status: number;
  holiday_description?: string;
  holiday_start_date?: string;
  holiday_end_date?: string;
  holiday_type?: string;
}

const Holidays: React.FC = () => {
  const [holidays, setHolidays] = useState<Holidays[]>([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<Holidays | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});

  const [isImportModalVisible, setIsImportModalVisible] = useState(false);

  const handleImportSuccess = () => {
    fetchHolidays();
  };
  const handleExport = async (format: 'csv' | 'xlsx') => {
    try {
      const blob = await apiRequest<Blob>({
        method: 'get',
        url: `/export-holidays?format=${format}`,
        config: { responseType: 'blob' },
      });

      const fileName = format === 'csv' ? 'holidays_export.csv' : 'holidays_export.xlsx';

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      showError('Failed to export holidays');
      console.error(error);
    }
  };
  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchHolidays = async () => {
    try {
      const data = await apiRequest<Holidays[]>({
        method: 'get',
        url: 'holidays',
      });
      setHolidays(data);
    } catch (error) {
      showError('Failed to fetch holidays');
      console.error(error);
    }
  };
  const exportMenu = (
    <Menu
      onClick={({ key }) => handleExport(key as 'csv' | 'xlsx')}
      items={[
        { key: 'csv', label: 'Export as CSV' },
        { key: 'xlsx', label: 'Export as Excel' },
      ]}
    />
  );
  useEffect(() => {
    fetchHolidays();
  }, []);

  const handleEditClick = (record: Holidays) => {
    setIsEditMode(true);
    setInitialValues({
      holiday_id: record.holiday_id,
      holiday_name: record.holiday_name,
      holiday_status: record.holiday_status,
      holiday_description: record.holiday_description,
      holiday_start_date: record.holiday_start_date,
      holiday_end_date: record.holiday_end_date,
      holiday_type: record.holiday_type,
    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchHolidays();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (holiday_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-holiday/${holiday_id}`,
      });
      showSuccess('holiday deleted');
      fetchHolidays();
    } catch {
      showError('Failed to delete holiday');
    }
  };

  const handleStatusToggle = async (HolidayIDs: number) => {
    setHolidays((prev) =>
      prev.map((item) =>
        item.holiday_id === HolidayIDs
          ? { ...item, holiday_status: item.holiday_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-holiday/${HolidayIDs}`,
      });
      showSuccess('Holiday status updated');
    } catch (error) {
      showError('Failed to update status');
      setHolidays((prev) =>
        prev.map((item) =>
          item.holiday_id === HolidayIDs
            ? { ...item, holiday_status: item.holiday_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<Holidays>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };




  const columns: TableColumnsType<Holidays> = [
    {
      title: 'Holiday Name',
      dataIndex: 'holiday_name',
      key: 'holiday_name',
      filters: Array.from(new Set(holidays.map((s) => s.holiday_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.holiday_name || null,
      onFilter: (value, record) => record.holiday_name.includes(value as string),
      sorter: (a, b) => a.holiday_name.localeCompare(b.holiday_name),
      sortOrder: sortedInfo.columnKey === 'holiday_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
    },
    {
      title: 'Type',
      dataIndex: 'holiday_type',
      key: 'holiday_type',
      filters: Array.from(new Set(holidays.map(h => h.holiday_type ?? 'Unknown'))).map((type) => ({
        text: type,
        value: type,
      })),
      onFilter: (value, record) => (record.holiday_type ?? 'Unknown') === value,
      ellipsis: true,
    },
    {
      title: 'Start Date',
      dataIndex: 'holiday_start_date',
      key: 'holiday_start_date',
      render: (date: string) => date || '-',
      sorter: (a, b) =>
        new Date(a.holiday_start_date || '').getTime() - new Date(b.holiday_start_date || '').getTime(),
      sortOrder: sortedInfo.columnKey === 'holiday_start_date' ? sortedInfo.order : null,
      ellipsis: true,
    },
    {
      title: 'End Date',
      dataIndex: 'holiday_end_date',
      key: 'holiday_end_date',
      render: (date: string) => date || '-',
      sorter: (a, b) =>
        new Date(a.holiday_end_date || '').getTime() - new Date(b.holiday_end_date || '').getTime(),
      sortOrder: sortedInfo.columnKey === 'holiday_end_date' ? sortedInfo.order : null,
      ellipsis: true,
    },
    {
      title: 'Description',
      dataIndex: 'holiday_description',
      key: 'holiday_description',
      ellipsis: true,
      render: (text: string | undefined) => text || '-',
    },
  ];



  // Conditionally add action column
  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      render: (_: any, record: Holidays) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.holiday_status}
                onChange={() => handleStatusToggle(record.holiday_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this holiday?"
              onConfirm={() => handleDelete(record.holiday_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }


  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Holidays</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Flex gap="small">
            <Button type="primary" onClick={showCreateModal}>
              Add Holiday
            </Button>
            <Button
              type="default"
              onClick={() => setIsImportModalVisible(true)}
            >
              Import Holidays
            </Button>
            <Dropdown overlay={exportMenu} placement="bottomRight" arrow>
              <Button type="default">
                Export Holidays <DownOutlined />
              </Button>
            </Dropdown>
          </Flex>
        )}
      </Space>


      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<Holidays>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="holiday_id"
          dataSource={holidays.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 11 }}
          locale={{ emptyText: 'No holiday found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, holidays.length)} of{' '}
                {holidays.length}
              </span>
              <Pagination
                {...pagination}
                total={holidays.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <HolidayModel
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
      />
      <ImportHolidayModal
        visible={isImportModalVisible}
        onClose={() => setIsImportModalVisible(false)}
        onSuccess={handleImportSuccess}
      />
    </>
  );
};

export default Holidays;